import React, { useState, useRef, useCallback, useEffect } from "react";
import { useVariables } from "../store/VariableStore";
import VariableAutocomplete from "./VariableAutocomplete";
import VariableTooltip from "./VariableTooltip";
import "./VariableInput.css";

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const SOURCE_CLASS = {
  global: "vi-var--global",
  environment: "vi-var--environment",
  dynamic: "vi-var--dynamic",
  local: "vi-var--local",
  unknown: "vi-var--unknown",
};

function buildHTML(parts) {
  return parts
    .map((part) => {
      if (part.type === "text") {
        return escapeHtml(part.text).replace(/\n/g, "<br>");
      }
      const cls = SOURCE_CLASS[part.source] || SOURCE_CLASS.unknown;
      return `<span class="vi-var ${cls}" data-var="${escapeHtml(part.name)}" data-source="${escapeHtml(part.source)}">${escapeHtml(part.raw)}</span>`;
    })
    .join("");
}

function getCursorOffset(el) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return 0;
  const range = sel.getRangeAt(0);

  let offset = 0;
  let found = false;

  function walk(node) {
    if (found) return;
    if (node === range.endContainer) {
      if (node.nodeType === Node.TEXT_NODE) {
        offset += range.endOffset;
      } else {
        Array.from(node.childNodes).slice(0, range.endOffset).forEach(walk);
      }
      found = true;
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      offset += node.length;
    } else if (node.nodeName === "BR") {
      offset += 1;
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        walk(node.childNodes[i]);
        if (found) return;
      }
    }
  }

  walk(el);
  return offset;
}

function setCursorOffset(el, targetOffset) {
  const sel = window.getSelection();
  if (!sel) return;

  let charCount = 0;
  let found = false;

  function walk(node) {
    if (found) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.length;
      if (charCount + len >= targetOffset) {
        const range = document.createRange();
        range.setStart(node, targetOffset - charCount);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        found = true;
        return;
      }
      charCount += len;
    } else if (node.nodeName === "BR") {
      if (charCount + 1 >= targetOffset) {
        const range = document.createRange();
        if (charCount >= targetOffset) range.setStartBefore(node);
        else range.setStartAfter(node);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        found = true;
        return;
      }
      charCount += 1;
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        walk(node.childNodes[i]);
        if (found) return;
      }
    }
  }

  walk(el);

  if (!found) {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

const VariableInput = ({
  value,
  onChange,
  placeholder = "",
  className = "",
  multiline = false,
  disabled = false,
  showAutocomplete = true,
  style,
  id,
  ...rest
}) => {
  const { tokenize } = useVariables();
  const editorRef = useRef(null);
  const internalValue = useRef(value || "");
  const isComposing = useRef(false);
  const suppressNextSync = useRef(false);

  const [acQuery, setAcQuery] = useState("");
  const [acPosition, setAcPosition] = useState({ x: 0, y: 0 });
  const [showAc, setShowAc] = useState(false);
  const [tooltip, setTooltip] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    variable: null,
  });
  const hideTooltipTimer = useRef(null);
  const showTooltipTimer = useRef(null);

  const rebuildHTML = useCallback(
    (text, preserveCursor) => {
      const el = editorRef.current;
      if (!el) return;

      const cursorOffset = preserveCursor ? getCursorOffset(el) : 0;

      const parts = tokenize(text || "");
      const newHTML = buildHTML(parts);

      if (el.innerHTML !== newHTML) {
        el.innerHTML = newHTML;
      }

      if (preserveCursor) {
        setCursorOffset(el, cursorOffset);
      }
    },
    [tokenize],
  );

  useEffect(() => {
    rebuildHTML(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (suppressNextSync.current) {
      suppressNextSync.current = false;
      return;
    }
    if ((value || "") === internalValue.current) return;
    internalValue.current = value || "";
    rebuildHTML(value || "", false);
  }, [value, rebuildHTML]);

  const getAcPosition = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return { x: 0, y: 0 };
    const r = sel.getRangeAt(0).cloneRange();
    r.collapse(true);
    const rect = r.getBoundingClientRect();
    return { x: rect.left, y: rect.bottom + 4 };
  }, []);

  const checkAutocompleteTrigger = useCallback(
    (text, cursorOffset) => {
      if (!showAutocomplete) return;
      const before = text.substring(0, cursorOffset);
      const lastOpen = before.lastIndexOf("{{");
      if (lastOpen === -1 || before.substring(lastOpen + 2).includes("}}")) {
        setShowAc(false);
        return;
      }
      setAcPosition(getAcPosition());
      setAcQuery(before.substring(lastOpen + 2));
      setShowAc(true);
    },
    [showAutocomplete, getAcPosition],
  );

  const handleInput = useCallback(() => {
    if (isComposing.current) return;
    const el = editorRef.current;
    if (!el) return;

    const cursorOffset = getCursorOffset(el);

    let text = el.innerText || "";

    if (text.endsWith("\n") && text.length > 1) text = text.slice(0, -1);

    if (text === "\n") text = "";

    internalValue.current = text;
    suppressNextSync.current = true;

    rebuildHTML(text, false);
    setCursorOffset(el, cursorOffset);

    if (onChange) onChange(text);
    checkAutocompleteTrigger(text, cursorOffset);
  }, [onChange, rebuildHTML, checkAutocompleteTrigger]);

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");

      if (!document.execCommand("insertText", false, text)) {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        sel.deleteFromDocument();
        const node = document.createTextNode(text);
        sel.getRangeAt(0).insertNode(node);
        sel.collapseToEnd();
      }

      handleInput();
    },
    [handleInput],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && (!multiline || !showAc)) {
        e.preventDefault();
        if (multiline) document.execCommand("insertLineBreak");
      }
      if (e.key === "Escape") {
        setShowAc(false);
      }
    },
    [multiline, showAc],
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => setShowAc(false), 150);
  }, []);

  const handleSelect = useCallback(
    (variableName) => {
      const el = editorRef.current;
      if (!el) return;

      const text = internalValue.current;
      const cursorOffset = getCursorOffset(el);
      const before = text.substring(0, cursorOffset);
      const after = text.substring(cursorOffset);
      const lastOpen = before.lastIndexOf("{{");

      const newVal =
        lastOpen !== -1
          ? before.substring(0, lastOpen + 2) + variableName + "}}" + after
          : text + "{{" + variableName + "}}";

      internalValue.current = newVal;
      suppressNextSync.current = true;

      rebuildHTML(newVal, false);
      const insertEnd = (lastOpen !== -1 ? lastOpen : text.length) + variableName.length + 4;
      setCursorOffset(el, insertEnd);

      setShowAc(false);
      if (onChange) onChange(newVal);
      el.focus();
    },
    [onChange, rebuildHTML],
  );

  const handleMouseOver = useCallback(
    (e) => {
      const span = e.target.closest(".vi-var");
      if (!span) return;
      const varName = span.getAttribute("data-var");
      const source = span.getAttribute("data-source");
      if (!varName) return;

      clearTimeout(hideTooltipTimer.current);
      clearTimeout(showTooltipTimer.current);
      showTooltipTimer.current = setTimeout(() => {
        const parts = tokenize(internalValue.current);
        const part = parts.find(
          (p) => p.type === "variable" && p.name === varName && p.source === source,
        );
        if (!part) return;
        const rect = span.getBoundingClientRect();
        setTooltip({
          isVisible: true,
          position: { x: rect.left + rect.width / 2, y: rect.bottom },
          variable: part,
        });
      }, 150);
    },
    [tokenize],
  );

  const handleMouseOut = useCallback((e) => {
    if (!e.target.closest(".vi-var")) return;
    clearTimeout(showTooltipTimer.current);
    hideTooltipTimer.current = setTimeout(
      () => setTooltip((prev) => ({ ...prev, isVisible: false })),
      300,
    );
  }, []);

  const handleCompositionStart = useCallback(() => {
    isComposing.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposing.current = false;
    handleInput();
  }, [handleInput]);

  return (
    <div className={`vi-wrapper${multiline ? " vi-wrapper--multiline" : ""}`}>
      <div
        ref={editorRef}
        id={id}
        contentEditable={disabled ? "false" : "true"}
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        data-placeholder={placeholder}
        className={`vi-editor${className ? ` ${className}` : ""}`}
        style={style}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />

      {showAc && showAutocomplete && (
        <VariableAutocomplete
          query={acQuery}
          position={acPosition}
          onSelect={handleSelect}
          onClose={() => setShowAc(false)}
        />
      )}

      <VariableTooltip
        isVisible={tooltip.isVisible}
        position={tooltip.position}
        variable={tooltip.variable}
        onMouseEnter={() => {
          clearTimeout(hideTooltipTimer.current);
          clearTimeout(showTooltipTimer.current);
        }}
        onMouseLeave={() => setTooltip((prev) => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
};

export default VariableInput;
